import { useState, useEffect, useRef } from "react";
import { callDashboardRoutesBatch } from "../../../api/dashboardV2Api";
import { GRID_METRIC_REGISTRY } from "../customizer/gridMetricRegistry";
import { TIER_OF } from "../metricTiers";

const REGISTRY_MAP = Object.fromEntries(
  GRID_METRIC_REGISTRY.map((m) => [m.id, m]),
);

function seedEntry(id) {
  const config = REGISTRY_MAP[id];
  return { chartType: config?.chartType ?? "coming_soon", data: [] };
}

function applyBundleResponse(res, ids, registryMap) {
  const dataUpdates = {};
  if (!res?.success) return dataUpdates;

  Object.entries(res.data?.graphs ?? {}).forEach(([id, rows]) => {
    if (!ids.includes(id)) return;
    dataUpdates[id] = Array.isArray(rows) && rows.length > 0
      ? { chartType: registryMap[id]?.chartType ?? "coming_soon", data: rows }
      : { chartType: "no_data", data: [] };
  });

  Object.entries(res.data?.ranked ?? {}).forEach(([id, payload]) => {
    if (!ids.includes(id)) return;
    const rows = Array.isArray(payload?.rows) ? payload.rows : [];
    dataUpdates[id] = rows.length > 0
      ? { chartType: registryMap[id]?.chartType ?? "coming_soon", data: rows }
      : { chartType: "no_data", data: [] };
  });

  return dataUpdates;
}

export function useGridMetricData(activeIds, filters) {
  const [data, setData] = useState(() =>
    Object.fromEntries(activeIds.map((id) => [id, seedEntry(id)])),
  );
  const [loading, setLoading] = useState({});

  const filtersKey = JSON.stringify(filters);
  const idsKey = JSON.stringify(activeIds);
  const prevKeys = useRef({ filtersKey: null, idsKey: null });
  const prevIds = useRef([]);

  useEffect(() => {
    // Seed any newly-active IDs that aren't in state yet
    setData((prev) => {
      const next = { ...prev };
      activeIds.forEach((id) => {
        if (!next[id]) next[id] = seedEntry(id);
      });
      return next;
    });

    if (activeIds.length === 0) return;

    const filtersChanged = prevKeys.current.filtersKey !== filtersKey;
    const idsChanged = prevKeys.current.idsKey !== idsKey;

    if (!filtersChanged && !idsChanged) return;

    // Filters changed → refetch everything (cached data is stale)
    // Only IDs changed → fetch only the newly added IDs
    const toFetch = filtersChanged
      ? activeIds
      : activeIds.filter((id) => !prevIds.current.includes(id));

    prevKeys.current = { filtersKey, idsKey };
    prevIds.current = activeIds;

    if (toFetch.length === 0) return;

    setLoading((prev) => ({
      ...prev,
      ...Object.fromEntries(toFetch.map((id) => [id, true])),
    }));

    // Split by tier: cheap+medium fire together, heavy in a separate request.
    // Both bundles fire in parallel — heavy widgets show spinners until their
    // bundle resolves while cheap/medium widgets render immediately.
    const fast = toFetch.filter((id) => (TIER_OF[id] ?? "medium") !== "heavy");
    const heavy = toFetch.filter((id) => TIER_OF[id] === "heavy");

    const requests = [
      fast.length > 0 ? callDashboardRoutesBatch(fast, filters) : null,
      heavy.length > 0 ? callDashboardRoutesBatch(heavy, filters) : null,
    ];

    Promise.allSettled(requests.filter(Boolean)).then((results) => {
      const dataUpdates = {};
      const loadUpdates = {};

      results.forEach((result) => {
        if (result.status === "fulfilled") {
          Object.assign(dataUpdates, applyBundleResponse(result.value, toFetch, REGISTRY_MAP));
        }
      });

      // Any ID not present in the responses (failed or no_data) gets a fallback
      toFetch.forEach((id) => {
        if (!dataUpdates[id]) dataUpdates[id] = { chartType: "no_data", data: [] };
        loadUpdates[id] = false;
      });

      setData((prev) => ({ ...prev, ...dataUpdates }));
      setLoading((prev) => ({ ...prev, ...loadUpdates }));
    });
  }, [idsKey, filtersKey]);

  return { data, loading };
}
